import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { X } from "react-feather";
import { all_routes } from "../../../Router/all_routes";
import { base_api_url } from "../../../environment.jsx";
import { useNavigate } from "react-router-dom";
import PropTypes from 'prop-types';
import './AddRestaurantNew.css';

/**
 * Composant AddRestaurantNew pour la création et modification d'un restaurant.
 * Basé sur la maquette Premium fournie.
 */
const AddRestaurantNew = React.forwardRef(({ onFormSubmit, restaurantToEdit = null }, ref) => {
    const MySwal = withReactContent(Swal);
    const navigate = useNavigate();
    const route = all_routes;
    const [chargement, setChargement] = useState(false);
    const isEdit = !!restaurantToEdit;
    const [logoPreview, setLogoPreview] = useState(null);
    const logoRef = useRef();

    /**
     * Convertit la valeur de devise de l'API vers le format d'affichage
     */
    const getDeviseDisplayValue = (deviseApi) => {
        if (!deviseApi) return "Euro (EUR)";

        const deviseMap = {
            "EUR": "Euro (EUR)",
            "XAF": "Franc CFA (XAF)",
            "XOF": "Franc CFA (XOF)",
            "USD": "Dollar (USD)"
        };

        return deviseMap[deviseApi] || `${deviseApi} (${deviseApi})`;
    };

    // État du formulaire
    const [formData, setFormData] = useState({
        nom: "",
        pays: "France",
        ville: "Paris",
        adresse: "",
        telephone: "",
        description: "",
        fuseau: "Europe/Paris (UTC+1/+2)",
        devise: "Euro (EUR)",
        services: ["Sur place"],
        logo: "",
        prise_cmd: ""
    });

    // Effet pour pré-remplir le formulaire
    useEffect(() => {
        if (restaurantToEdit) {
            setFormData({
                nom: restaurantToEdit.nom || "",
                pays: restaurantToEdit.pays || "France",
                ville: restaurantToEdit.ville || "Paris",
                adresse: restaurantToEdit.adresse || "",
                telephone: restaurantToEdit.telephone || "",
                description: restaurantToEdit.description || "",
                fuseau: restaurantToEdit.fuseau || "Europe/Paris (UTC+1/+2)",
                devise: getDeviseDisplayValue(restaurantToEdit.devise) || "Euro (EUR)",
                services: restaurantToEdit.services || ["Sur place"],
                logo: restaurantToEdit.logo || "",
                prise_cmd: restaurantToEdit.prise_cmd || ""
            });
            if (restaurantToEdit.logo) {
                setLogoPreview(restaurantToEdit.logo);
            }
        } else {
            setFormData({
                nom: "",
                pays: "France",
                ville: "Paris",
                adresse: "",
                telephone: "",
                description: "",
                fuseau: "Europe/Paris (UTC+1/+2)",
                devise: "Euro (EUR)",
                services: ["Sur place"],
                logo: "",
                prise_cmd: ""
            });
            setLogoPreview(null);
        }
    }, [restaurantToEdit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        if (isEdit && restaurantToEdit) {
            setFormData({
                nom: restaurantToEdit.nom || "",
                pays: restaurantToEdit.pays || "France",
                ville: restaurantToEdit.ville || "Paris",
                adresse: restaurantToEdit.adresse || "",
                telephone: restaurantToEdit.telephone || "",
                description: restaurantToEdit.description || "",
                fuseau: restaurantToEdit.fuseau || "Europe/Paris (UTC+1/+2)",
                devise: restaurantToEdit.devise || "Euro (EUR)",
                services: restaurantToEdit.services || ["Sur place"],
                logo: restaurantToEdit.logo || "",
                prise_cmd: restaurantToEdit.prise_cmd || ""
            });
            setLogoPreview(restaurantToEdit.logo || null);
        } else {
            setFormData({
                nom: "",
                pays: "France",
                ville: "Paris",
                adresse: "",
                telephone: "",
                description: "",
                fuseau: "Europe/Paris (UTC+1/+2)",
                devise: "Euro (EUR)",
                services: ["Sur place"],
                logo: "",
                prise_cmd: ""
            });
            if (logoRef.current) logoRef.current.value = "";
            setLogoPreview(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.nom.trim()) {
            MySwal.fire({
                icon: "warning",
                text: "Veuillez renseigner le nom du restaurant.",
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 3000,
                customClass: { popup: "pixel-toast" }
            });
            return;
        }

        setChargement(true);
        try {
            const token = JSON.parse(localStorage.getItem("user"))?.access_token;
            const id_utilisateur = JSON.parse(localStorage.getItem("user"))?.id_utilisateur;

            // Upload logo si un nouveau fichier a été sélectionné
            let logoFileName = formData.logo;
            if (logoPreview && logoPreview.startsWith('blob:')) {
                const logoFile = logoRef.current?.files[0];
                if (logoFile) {
                    const logoFormData = new FormData();
                    logoFormData.append('file', logoFile);
                    logoFormData.append('nomtableconcerner', 'restaurant');
                    logoFormData.append('iduser', id_utilisateur);
                    try {
                        const uploadResponse = await axios.post(
                            `${base_api_url}api/envoyer_image`,
                            logoFormData,
                            { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` } }
                        );
                        if (uploadResponse.data.nom_image) {
                            logoFileName = uploadResponse.data.nom_image;
                        }
                    } catch (uploadError) {
                        console.error("Erreur upload logo:", uploadError);
                        throw new Error("Échec du téléchargement du logo. Veuillez réessayer.");
                    }
                }
            }

            const apiData = {
                nom: formData.nom,
                adresse: formData.adresse || `${formData.ville}, ${formData.pays}`,
                devise: formData.devise.includes('(') ? formData.devise.split('(')[1]?.replace(')', '') : formData.devise,
                telephone: formData.telephone || "",
                description: formData.description || "",
                logo: logoFileName || null,
                prise_cmd: formData.prise_cmd && formData.prise_cmd.length === 5 && /^\d{2}:\d{2}$/.test(formData.prise_cmd)
                    ? `1970-01-01 ${formData.prise_cmd}:00`
                    : formData.prise_cmd || null
            };

            let response;
            if (isEdit) {
                response = await axios.put(`${base_api_url}api/restaurant/${restaurantToEdit.id_restaurant}`, apiData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                const createData = {
                    ...apiData,
                    pays: formData.pays,
                    ville: formData.ville,
                    fuseau: formData.fuseau,
                    services: formData.services
                };
                response = await axios.post(`${base_api_url}api/restaurant`, createData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            if (response.status === 200 || response.status === 201) {
                MySwal.fire({
                    icon: "success",
                    text: isEdit ? "Les modifications ont été enregistrées." : "Votre nouvel établissement est prêt.",
                    toast: true,
                    position: "bottom-end",
                    showConfirmButton: false,
                    timer: 3000,
                    customClass: { popup: "pixel-toast" }
                });

                resetForm();
                onFormSubmit();

                const modalElement = document.getElementById('new-restau');
                if (modalElement) {
                    const closeButton = modalElement.querySelector('[data-bs-dismiss="modal"]');
                    if (closeButton) {
                        closeButton.click();
                    } else {
                        modalElement.classList.remove('show');
                        modalElement.style.display = 'none';
                        modalElement.setAttribute('aria-hidden', 'true');
                        modalElement.removeAttribute('aria-modal');
                        const backdrop = document.querySelector('.modal-backdrop');
                        if (backdrop) backdrop.remove();
                        document.body.classList.remove('modal-open');
                        document.body.style.overflow = '';
                        document.body.style.paddingRight = '';
                    }
                }

                if (!isEdit) {
                    // Mettre à jour l'utilisateur avec le nouveau restoId pour la Sidebar
                    const userStr = localStorage.getItem("user");
                    if (userStr) {
                        const user = JSON.parse(userStr);
                        // L'API renvoie l'objet restaurant créé
                        const newRestoId = response.data.id_restaurant || response.data.restaurant?.id_restaurant;
                        if (newRestoId) {
                            user.restoId = newRestoId;
                            localStorage.setItem("user", JSON.stringify(user));
                        }
                    }
                    localStorage.setItem("restaurant", JSON.stringify(response.data));

                    // Notifier les composants (Sidebar, Header, etc.) du changement
                    window.dispatchEvent(new Event("restaurantChanged"));

                    setTimeout(() => navigate(route.Restaurants), 2000);
                }
            }
        } catch (error) {
            console.error(`Erreur ${isEdit ? 'modification' : 'création'} restaurant:`, error);
            MySwal.fire({
                icon: "error",
                text: error.response?.data?.message || `Une erreur est survenue lors de la ${isEdit ? 'modification' : 'création'}.`,
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 5000,
                customClass: { popup: "pixel-toast" }
            });
        } finally {
            setChargement(false);
        }
    };

    return (
        <div ref={ref} className="modal fade add-restaurant-modal" id="new-restau" tabIndex="-1" aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form onSubmit={handleSubmit} className="modal-content">
                    <div className="modal-header border-0">
                        <h5 className="modal-title">{isEdit ? "Modifier le restaurant" : "Ajouter un restaurant"}</h5>
                        <button
                            type="button"
                            className="btn-close-custom"
                            data-bs-dismiss="modal"
                            onClick={resetForm}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="modal-body">
                        <div className="modal-body-content row g-3 m-0">
                            {/* Logo Upload Section */}
                            <div className="col-12 mb-2">
                                <label className="form-label-caps">LOGO DU RESTAURANT</label>
                                <div className="logo-upload-area">
                                    {logoPreview ? (
                                        <div className="upload-content preview-active">
                                            <img
                                                src={logoPreview.startsWith('qr100-') ? `${base_api_url}doc/recuperer_fichier/${logoPreview}` : logoPreview}
                                                alt="Aperçu logo"
                                                className="logo-preview-img"
                                            />
                                            <div className="upload-info-overlay">
                                                <span className="upload-title">Logo selectionné</span>
                                                <span className="upload-subtitle">Cliquer pour changer</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="upload-content">
                                            <div className="upload-icon-box">
                                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
                                            </div>
                                            <span className="upload-title">Cliquez pour ajouter une image</span>
                                            <span className="upload-subtitle">PNG, JPG jusqu&apos;à 5 Mo</span>
                                        </div>
                                    )}
                                    {/* Input DANS logo-upload-area : position:absolute le confine à cette zone uniquement */}
                                    <input
                                        type="file"
                                        className="logo-input-file"
                                        accept="image/*"
                                        ref={logoRef}
                                        onChange={(e) => {
                                            if (e.target.files && e.target.files[0]) {
                                                const file = e.target.files[0];

                                                // Validation du type de fichier
                                                const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
                                                if (!allowedTypes.includes(file.type)) {
                                                    MySwal.fire({
                                                        icon: "error",
                                                        text: `Format de fichier non supporté. Veuillez choisir une image au format : ${allowedTypes.map(t => t.split('/')[1].toUpperCase()).join(', ')}`,
                                                        toast: true,
                                                        position: "bottom-end",
                                                        showConfirmButton: false,
                                                        timer: 4000,
                                                        customClass: { popup: "pixel-toast" }
                                                    });
                                                    e.target.value = "";
                                                    return;
                                                }

                                                // Validation de la taille (5 Mo max)
                                                const maxSize = 5 * 1024 * 1024;
                                                if (file.size > maxSize) {
                                                    MySwal.fire({
                                                        icon: "error",
                                                        text: `L'image dépasse la limite de 5 Mo. Taille actuelle : ${(file.size / (1024 * 1024)).toFixed(2)} Mo.`,
                                                        toast: true,
                                                        position: "bottom-end",
                                                        showConfirmButton: false,
                                                        timer: 4000,
                                                        customClass: { popup: "pixel-toast" }
                                                    });
                                                    e.target.value = "";
                                                    return;
                                                }

                                                setLogoPreview(URL.createObjectURL(file));
                                                setFormData(prev => ({ ...prev, logo: URL.createObjectURL(file) }));
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="col-12">
                                <label className="form-label-caps">NOM DU RESTAURANT *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="nom"
                                    placeholder="Ex: La Sanaga"
                                    value={formData.nom}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label-caps">PAYS *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="pays"
                                    value={formData.pays}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label-caps">VILLE *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="ville"
                                    value={formData.ville}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label-caps">ADRESSE</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="adresse"
                                    placeholder="Ex: 123 Rue de la Paix"
                                    value={formData.adresse}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label-caps">TÉLÉPHONE</label>
                                <input
                                    type="tel"
                                    className="form-control"
                                    name="telephone"
                                    placeholder="Ex: +33 1 23 45 67 89"
                                    value={formData.telephone}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label-caps">DEVISE *</label>
                                <div className="custom-select-wrapper">
                                    <select
                                        className="form-select"
                                        name="devise"
                                        value={formData.devise}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="EUR">Euro (EUR)</option>
                                        <option value="XAF">Franc CFA (XAF)</option>
                                        <option value="XOF">Franc CFA (XOF)</option>
                                        <option value="USD">Dollar (USD)</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-12 mb-3">
                                <label className="form-label-caps">DESCRIPTION</label>
                                <textarea
                                    className="form-control"
                                    name="description"
                                    rows="3"
                                    placeholder="Décrivez votre restaurant..."
                                    value={formData.description}
                                    onChange={handleChange}
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer border-0">
                        <div className="modal-actions-stack">
                            <button
                                type="submit"
                                className="btn-primary-full"
                                disabled={chargement}
                            >
                                {chargement ? (isEdit ? "Modification..." : "Création...") : (isEdit ? "Modifier" : "Ajouter")}
                            </button>
                            <button
                                type="button"
                                className="btn-secondary-full"
                                data-bs-dismiss="modal"
                                onClick={resetForm}
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
});

AddRestaurantNew.displayName = "AddRestaurantNew";
AddRestaurantNew.propTypes = {
    onFormSubmit: PropTypes.func.isRequired,
    restaurantToEdit: PropTypes.object,
};

export default AddRestaurantNew;
